import { useCallback, useEffect, useRef, useState } from 'react';
import { FinanceDrawer, FinanceFile, FinancePagination } from './FinancePrimitives';
import { FINANCE_UI as F, type FinanceView } from './metin/finans-ui';
import {
  api,
  ApiError,
  type Coupon,
  type CouponDetail,
  type CouponListPage,
  type CouponNotificationPage,
  type GezipuanLedger,
  type GezipuanLiability,
  type InfluencerEarning,
  type InvoiceQueueDetail,
  type InvoiceQueuePage,
  type InfluencerEarningDetail,
  type InfluencerEarningListPage,
  type MarginRule,
  type Me,
  type InvoiceMarginPage,
  type OrderInvoiceListPage,
  type PurchaseInvoiceListPage,
  type PurchaseInvoiceRow,
  type SalesInvoiceRecordInput,
  type SupplierPayableListPage,
  type SupplierPaymentDetail,
} from './api';
import { Alan, AlanSatiri, describe as hataCumlesi, ondalikTamSayiya, paraAltBirime, tamSayiOndaliga } from './Workflow';
import { indirimYaz, KALEM_TURU, kalemTuru, ORTAK, para, tamSayi, tarih, tarihSaat, TANIMSIZ } from './metin/ortak';
import {
  ALIS_TURU,
  FATURA_KAPISI,
  FATURA_SATIR_TURU,
  FINANS_METIN,
  HAKEDIS_DURUMU,
  KESILECEK_FATURA_DURUMU,
  KIMLIK_KAYNAGI,
  KUR_KAYNAGI,
  ITIRAZ_SONUCU,
  KESILEN_FATURA_DURUMU,
  KUPON_DURUMU,
  REZERVASYON_DURUMU,
  TEDARIKCI_ODEME_DURUMU,
  YAYIN_DURUMU,
} from './metin/finans';

/**
 * CRM-A25: Finans bölümünün influencer hakedişi, kupon ve GeziPuan borcu ekranları.
 *
 * Ölçüldü (27.08.2026): Finans bölümünün React tarafı yalnız sipariş bazlı `FinancePanel`i
 * taşıyordu (`Workflow.tsx`) — ödeme, maliyet, iade, mutabakat. Kampanya akışı vakasının 17-26.
 * adımları (hakediş defteri) ve kupon akışı vakasının §5'i hiç ekran taşımıyordu, uç zaten
 * açıktı ama panelden hiç çağrılmıyordu.
 *
 * Üç bölüm de kendi verisi **sıfır satır** olabilir. Hakediş tarafı artık gerçekten yazılıyor:
 * köprü 27.08.2026'da uçtan uca ölçüldü ve influencer platformu iki hakedişi
 * `POST /api/v1/finans/hakedisler` ucuna yazdı, ikisi de `ACCRUED` durumunda listeye düştü.
 * Kupon ve GeziPuan tarafında hâlâ boş tablo görülebilir. Boş tablo burada hep bir cümleyle
 * açıklanıyor: neden boş, kimin işi bitmedi. Boş bir tabloyla kullanıcı yalnız bırakılmıyor.
 */

const loadEarnings = (page: number, status: string) => api.influencerEarnings({ page, pageSize: 20, ...(status ? { status } : {}) });
const loadCoupons = (page: number, status: string) => api.coupons({ page, pageSize: 20, ...(status ? { status } : {}) });
const loadSuppliers = (page: number, paymentStatus: string) => api.payableSupplierCosts({ page, pageSize: 20, ...(paymentStatus ? { paymentStatus } : {}) });
const loadInvoices = (page: number, state: string) => api.orderInvoices({ page, pageSize: 20, ...(state ? { state } : {}) });

/** Never show stale rows or zero totals while a filtered request is pending or failed. */
function useFinanceLedger<T>(load: (page: number, status: string) => Promise<T>, fallback: string) {
  const [sayfa, setSayfa] = useState<T | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [durum, updateDurum] = useState('');
  const [sayfaNo, setSayfaNo] = useState(1);
  const sequence = useRef(0);
  const yukle = useCallback(async () => {
    const current = ++sequence.current;
    setHata(null); setYukleniyor(true);
    try { const result = await load(sayfaNo, durum); if (current === sequence.current) setSayfa(result); }
    catch (caught) { if (current === sequence.current) { setSayfa(null); setHata(hataMetni(caught, fallback)); } }
    finally { if (current === sequence.current) setYukleniyor(false); }
  }, [load, sayfaNo, durum, fallback]);
  useEffect(() => { void yukle(); return () => { sequence.current++; }; }, [yukle]);
  return { sayfa, hata, yukleniyor, durum, setDurum: (value: string) => { updateDurum(value); setSayfaNo(1); }, setSayfaNo, yukle };
}

function hataMetni(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) return caught.message;
  return fallback;
}

function karsilik(tablo: Readonly<Record<string, string>>, kod: string | null | undefined): string {
  if (kod === null || kod === undefined) return TANIMSIZ;
  return tablo[kod] ?? kod;
}

const HAKEDIS_DURUMLARI = ['ACCRUED', 'RECONCILIATION_SENT', 'DISPUTED', 'APPROVED', 'INVOICE_UPLOADED', 'PAYMENT_ORDERED', 'PAID'] as const;
const KUPON_DURUMLARI = ['ACTIVE', 'SUSPENDED'] as const;

/** Bir hakedişin ayrıntısı: durum geçmişi, itiraz, fatura ve ödeme emri formları. */
function HakedisAyrinti({
  id,
  canWrite,
  onDegisti,
  onKapat,
}: {
  id: string;
  canWrite: boolean;
  onDegisti: () => void;
  onKapat: () => void;
}) {
  const [detay, setDetay] = useState<InfluencerEarningDetail | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [tamam, setTamam] = useState<string | null>(null);
  const [itirazSebebi, setItirazSebebi] = useState('');
  const [itirazSonucu, setItirazSonucu] = useState<'NO_CHANGE' | 'CORRECTED'>('NO_CHANGE');
  const [duzeltilmisTutar, setDuzeltilmisTutar] = useState('');
  const [itirazNotu, setItirazNotu] = useState('');
  const [faturaAdi, setFaturaAdi] = useState('');
  const [faturaTuru, setFaturaTuru] = useState('application/pdf');
  const [faturaDosya, setFaturaDosya] = useState<File | null>(null);
  const [odemeReferansi, setOdemeReferansi] = useState('');
  const [odemeNotu, setOdemeNotu] = useState('');
  const metin = FINANS_METIN.hakedisler;

  const yukle = useCallback(async () => {
    setHata(null);
    try {
      setDetay(await api.influencerEarning(id));
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    }
  }, [id, metin.hata]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  const isle = async (eylem: () => Promise<InfluencerEarning>, basari: string) => {
    setHata(null);
    setTamam(null);
    try {
      await eylem();
      setTamam(basari);
      await yukle();
      onDegisti();
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    }
  };

  const earning = detay?.earning;

  return (
    <section className="content-section" data-testid="influencer-earning-detail" aria-labelledby="influencer-earning-detail-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="influencer-earning-detail-title">{metin.detay}</h2>
        </div>
        <button type="button" onClick={onKapat}>{metin.kapat}</button>
      </div>

      {hata ? <div className="error callout" role="alert">{hata}</div> : null}
      {tamam ? <div className="callout" role="status">{tamam}</div> : null}

      {earning ? (
        <>
          <div className="stat-row">
            <span>{metin.sutunDurum}: <strong data-testid="influencer-earning-status">{karsilik(HAKEDIS_DURUMU, earning.status)}</strong></span>
            <span>{metin.sutunInfluencer}: <strong>{earning.influencerName}</strong></span>
            <span>{metin.sutunKampanya}: <strong>{earning.campaignTitle}</strong></span>
            <span>{metin.sutunTutar}: <strong>{para(earning.amountMinor, earning.currencyCode)}</strong></span>
            <span>{metin.sutunDonem}: <strong>{tarih(earning.periodStart)} – {tarih(earning.periodEnd)}</strong></span>
            <span>{metin.sutunHesaplanma}: <strong>{tarihSaat(earning.computedAt)}</strong></span>
          </div>

          {earning.disputeReason ? (
            <p className="callout" role="note">
              {metin.itirazSebebi}: {earning.disputeReason}
              {earning.disputeResolution ? ` — ${karsilik(ITIRAZ_SONUCU, earning.disputeResolution)}` : ''}
            </p>
          ) : null}

          {canWrite && earning.status === 'ACCRUED' ? (
            <button type="button" data-testid="earning-send-reconciliation" onClick={() => void isle(() => api.sendInfluencerReconciliation(id), metin.mutabakataGonderildi)}>
              {metin.mutabakataGonder}
            </button>
          ) : null}

          {canWrite && earning.status === 'RECONCILIATION_SENT' ? (
            <>
              <button type="button" onClick={() => void isle(() => api.approveInfluencerEarning(id), metin.onaylandi)}>{metin.onayla}</button>
              <AlanSatiri>
                <Alan etiket={metin.itirazSebebi} zorunlu>
                  <input value={itirazSebebi} onChange={(olay) => setItirazSebebi(olay.target.value)} />
                </Alan>
              </AlanSatiri>
              <button
                type="button"
                data-testid="earning-dispute"
                onClick={() => void isle(async () => {
                  const result = await api.disputeInfluencerEarning(id, { reason: itirazSebebi });
                  setItirazSebebi('');
                  return result;
                }, metin.itirazKaydedildi)}
              >
                {metin.itirazEt}
              </button>
            </>
          ) : null}

          {canWrite && earning.status === 'DISPUTED' ? (
            <>
              <AlanSatiri>
                <Alan etiket={metin.itirazSonucu}>
                  <select value={itirazSonucu} onChange={(olay) => setItirazSonucu(olay.target.value as 'NO_CHANGE' | 'CORRECTED')}>
                    {(['NO_CHANGE', 'CORRECTED'] as const).map((deger) => <option key={deger} value={deger}>{ITIRAZ_SONUCU[deger]}</option>)}
                  </select>
                </Alan>
                {itirazSonucu === 'CORRECTED' ? (
                  <Alan etiket={metin.duzeltilmisTutar} ipucu={ORTAK.tutar} zorunlu>
                    <input inputMode="decimal" value={duzeltilmisTutar} onChange={(olay) => setDuzeltilmisTutar(olay.target.value)} />
                  </Alan>
                ) : null}
                <Alan etiket={metin.not}>
                  <input value={itirazNotu} onChange={(olay) => setItirazNotu(olay.target.value)} />
                </Alan>
              </AlanSatiri>
              <button
                type="button"
                data-testid="earning-resolve-dispute"
                onClick={() => void isle(() => api.resolveInfluencerDispute(id, {
                  outcome: itirazSonucu,
                  ...(itirazSonucu === 'CORRECTED' ? { correctedAmountMinor: Math.round(Number(duzeltilmisTutar.replace(',', '.')) * 100) } : {}),
                  ...(itirazNotu ? { note: itirazNotu } : {}),
                }), metin.itirazKapatildi)}
              >
                {metin.itiraziCoz}
              </button>
            </>
          ) : null}

          {canWrite && earning.status === 'APPROVED' ? (
            <>
              <details className="finance-disclosure"><summary>{F.addInvoice}</summary>
                <Alan etiket={metin.faturaIcerik} zorunlu><FinanceFile value={faturaDosya} onChange={(file) => { setFaturaDosya(file); if (file) { setFaturaAdi(file.name); setFaturaTuru(file.type || 'application/pdf'); } }} /></Alan>
                <details><summary>{F.fileDetails}</summary><AlanSatiri>
                  <Alan etiket={metin.faturaAdi} zorunlu><input value={faturaAdi} onChange={(olay) => setFaturaAdi(olay.target.value)} /></Alan>
                  <Alan etiket={metin.faturaTuru} zorunlu><input value={faturaTuru} onChange={(olay) => setFaturaTuru(olay.target.value)} /></Alan>
                </AlanSatiri></details>
              <button
                type="button"
                data-testid="earning-upload-invoice"
                disabled={!faturaDosya}
                onClick={() => void isle(async () => {
                  if (!faturaDosya) throw new Error(metin.faturaIcerik);
                  const contentBase64 = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
                    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
                    reader.readAsDataURL(faturaDosya);
                  });
                  await api.uploadInfluencerInvoice(id, { fileName: faturaAdi || faturaDosya.name, contentType: faturaTuru, contentBase64 });
                  setFaturaAdi('');
                  setFaturaDosya(null);
                  return earning;
                }, metin.faturaYuklendi)}
              >
                {metin.faturaYukle}
              </button></details>
            </>
          ) : null}

          {canWrite && earning.status === 'INVOICE_UPLOADED' ? (
            <>
              <AlanSatiri>
                <Alan etiket={metin.odemeReferansi} zorunlu>
                  <input value={odemeReferansi} onChange={(olay) => setOdemeReferansi(olay.target.value)} />
                </Alan>
                <Alan etiket={metin.not}>
                  <input value={odemeNotu} onChange={(olay) => setOdemeNotu(olay.target.value)} />
                </Alan>
              </AlanSatiri>
              <button
                type="button"
                data-testid="earning-payment-order"
                onClick={() => void isle(() => api.recordInfluencerPaymentOrder(id, { reference: odemeReferansi, ...(odemeNotu ? { note: odemeNotu } : {}) }), metin.odemeEmriGirildi)}
              >
                {metin.odemeEmriGir}
              </button>
            </>
          ) : null}

          {canWrite && earning.status === 'PAYMENT_ORDERED' ? (
            <button type="button" data-testid="earning-mark-paid" onClick={() => void isle(() => api.markInfluencerPaid(id, odemeNotu ? { note: odemeNotu } : {}), metin.odendiIsaretlendi)}>
              {metin.odendiIsaretle}
            </button>
          ) : null}

          <h3>{metin.faturalarBasligi}</h3>
          {detay.invoices.length === 0 ? (
            <p className="muted">{metin.faturaYok}</p>
          ) : (
            <ul>
              {detay.invoices.map((invoice) => (
                <li key={invoice.id}>
                  {invoice.fileName} · {tarihSaat(invoice.uploadedAt)}{' '}
                  <button type="button" onClick={() => void api.downloadInfluencerInvoice(invoice.id, invoice.fileName)}>{metin.faturaIndir}</button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}
    </section>
  );
}

/** Vaka madde 17-26: hakediş listesi ve seçili satırın ayrıntısı. */
function HakedislerBolumu({ canWrite }: { canWrite: boolean }) {
  const { sayfa, hata, yukleniyor, durum, setDurum, setSayfaNo, yukle } = useFinanceLedger<InfluencerEarningListPage>(loadEarnings, FINANS_METIN.hakedisler.hata);
  const [seciliId, setSeciliId] = useState<string | null>(null);
  const metin = FINANS_METIN.hakedisler;

  return (
    <section className="content-section" data-testid="influencer-earnings" aria-labelledby="influencer-earnings-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="influencer-earnings-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <span className="count-badge">{yukleniyor || hata || !sayfa ? TANIMSIZ : F.count(sayfa.total)}</span>
      </div>

      {hata ? <div className="error callout" role="alert">{hata}</div> : null}

      <div className="toolbar">
        <label>
          {metin.sutunDurum}{' '}
          <select value={durum} onChange={(olay) => setDurum(olay.target.value)}>
            <option value="">{metin.tumDurumlar}</option>
            {HAKEDIS_DURUMLARI.map((deger) => <option key={deger} value={deger}>{HAKEDIS_DURUMU[deger]}</option>)}
          </select>
        </label>
      <button type="button" disabled={yukleniyor} onClick={() => void yukle()}>{F.refresh}</button>
</div>

      <div className="table-wrap">
        <table data-testid="influencer-earnings-table">
          <thead>
            <tr>
              <th>{metin.sutunInfluencer}</th>
              <th>{metin.sutunKampanya}</th>
              <th className="money">{metin.sutunTutar}</th>
              <th>{metin.sutunDonem}</th>
              <th>{metin.sutunDurum}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {yukleniyor || hata || !sayfa ? <tr><td colSpan={6} className="muted" role="status">{yukleniyor ? F.loading : F.failed}</td></tr> : sayfa.items.length === 0 ? (
              <tr><td colSpan={6} className="muted">{metin.bos}</td></tr>
            ) : (
              sayfa?.items.map((earning) => (
                <tr key={earning.id} data-testid="influencer-earning-row">
                  <td data-label={metin.sutunInfluencer}>{earning.influencerName}</td>
                  <td data-label={metin.sutunKampanya}>{earning.campaignTitle}</td>
                  <td data-label={metin.sutunTutar} className="money">{para(earning.amountMinor, earning.currencyCode)}</td>
                  <td data-label={metin.sutunDonem}>{tarih(earning.periodStart)} – {tarih(earning.periodEnd)}</td>
                  <td data-label={metin.sutunDurum}>{karsilik(HAKEDIS_DURUMU, earning.status)}</td>
                  <td><button type="button" onClick={() => setSeciliId(earning.id)}>{metin.detay}</button></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!hata ? <FinancePagination page={sayfa} busy={yukleniyor} onPage={setSayfaNo} /> : null}

      {seciliId ? (
        <FinanceDrawer title={metin.detay} onClose={() => setSeciliId(null)}><HakedisAyrinti id={seciliId} canWrite={canWrite} onDegisti={() => void yukle()} onKapat={() => setSeciliId(null)} /></FinanceDrawer>
      ) : null}
    </section>
  );
}

/** Bir kuponun rezervasyon geçmişi ve yeniden yayınlama. */
function KuponAyrinti({ code, canWrite, onKapat }: { code: string; canWrite: boolean; onKapat: () => void }) {
  const [detay, setDetay] = useState<CouponDetail | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [tamam, setTamam] = useState<string | null>(null);
  const metin = FINANS_METIN.kuponlar;

  const yukle = useCallback(async () => {
    setHata(null);
    try {
      setDetay(await api.coupon(code));
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    }
  }, [code, metin.hata]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  return (
    <section className="content-section" data-testid="coupon-detail" aria-labelledby="coupon-detail-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="coupon-detail-title">{code}</h2>
        </div>
        <button type="button" onClick={onKapat}>{metin.kapat}</button>
      </div>

      {hata ? <div className="error callout" role="alert">{hata}</div> : null}
      {tamam ? <div className="callout" role="status">{tamam}</div> : null}

      {detay ? (
        <>
          <div className="stat-row">
            <span>{metin.sutunDurum}: <strong>{karsilik(KUPON_DURUMU, detay.status)}</strong></span>
            <span>{metin.sutunIndirim}: <strong>{indirimYaz(detay.discount)}</strong></span>
            <span>{metin.sutunYayin}: <strong>{karsilik(YAYIN_DURUMU, detay.publication.state)}</strong></span>
            <span>{metin.sutunBudget}: <strong>{detay.budget.remaining === null ? metin.sinirsiz : metin.kalan(tamSayi(detay.budget.remaining))}</strong></span>
          </div>
          {detay.publication.error ? <p className="callout" role="alert">{metin.yayinHatasi}: {detay.publication.error}</p> : null}
          {canWrite && detay.publication.state !== 'PUBLISHED' ? (
            <button
              type="button"
              data-testid="coupon-republish"
              onClick={async () => {
                setHata(null);
                try {
                  await api.publishCoupon(code);
                  setTamam(metin.yenidenYayinlandi);
                  await yukle();
                } catch (caught) {
                  setHata(hataMetni(caught, metin.hata));
                }
              }}
            >
              {metin.yenidenYayinla}
            </button>
          ) : null}

          <h3>{metin.sonRezervasyonlar}</h3>
          {detay.reservations.length === 0 ? (
            <p className="muted">{metin.rezervasyonYok}</p>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>{ORTAK.siparis}</th><th>{ORTAK.durum}</th><th>{ORTAK.tutar}</th></tr></thead>
                <tbody>
                  {detay.reservations.map((reservation) => (
                    <tr key={reservation.id}>
                      <td>{reservation.orderId}</td>
                      <td>{karsilik(REZERVASYON_DURUMU, reservation.state)}</td>
                      <td>{para(reservation.discountAmountMinor, reservation.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

/**
 * Kupon akışı vakası §5.3: influencer platformuna borçlu bildirimler.
 *
 * `kuyrukYazabilir` **`finance:write` değil `coupon:write`**. Ölçüldü 28.08.2026: "Kuyruğu şimdi
 * çalıştır" düğmesi hiçbir izne bakmadan çiziliyordu ve uç `coupon:write` istiyor. ADMIN ile
 * MANAGER `coupon:read` tutuyor, `coupon:write` tutmuyor — yani panelin sahibi düğmeyi görüyor,
 * basıyor ve 403 alıyordu. Bu depodaki kural, kullanılamayacak düğmeyi hiç çizmemek.
 */
function KuponBildirimleri({ kuyrukYazabilir }: { kuyrukYazabilir: boolean }) {
  const [sayfa, setSayfa] = useState<CouponNotificationPage | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [tamam, setTamam] = useState<string | null>(null);
  const metin = FINANS_METIN.kuponlar;

  const yukle = useCallback(async () => {
    setHata(null);
    try {
      setSayfa(await api.couponNotifications());
    } catch (caught) {
      setHata(hataMetni(caught, metin.hataBildirim));
    }
  }, [metin.hataBildirim]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  return (
    <section className="content-section" data-testid="coupon-notifications" aria-labelledby="coupon-notifications-title">
      <div className="section-heading">
        <div>
          <h3 id="coupon-notifications-title">{metin.bildirimlerBasligi}</h3>
          <p className="muted">{metin.bildirimlerTarif}</p>
        </div>
      </div>
      {hata ? <div className="error callout" role="alert">{hata}</div> : null}
      {tamam ? <div className="callout" role="status">{tamam}</div> : null}
      {sayfa ? (
        <>
          <div className="stat-row" data-testid="coupon-notifications-stats">
            <span>{metin.bekleyen}: <strong>{tamSayi(sayfa.total)}</strong></span>
            <span>{metin.basarisiz}: <strong>{tamSayi(sayfa.failed)}</strong></span>
            <span>{metin.teslimEdilen}: <strong>{tamSayi(sayfa.delivered)}</strong></span>
            {sayfa.oldestPendingAt ? <span>{metin.enEskiBekleyen}: <strong>{tarihSaat(sayfa.oldestPendingAt)}</strong></span> : null}
          </div>
          <p className={sayfa.delivery.state === 'NOT_CONFIGURED' ? 'callout' : 'muted'} role={sayfa.delivery.state === 'NOT_CONFIGURED' ? 'alert' : undefined}>
            {metin.gonderimDurumu[sayfa.delivery.state] ?? sayfa.delivery.detail}
          </p>
          {kuyrukYazabilir ? (
            <button
              type="button"
              data-testid="coupon-notifications-run"
              onClick={async () => {
                setHata(null);
                try {
                  const rapor = await api.deliverCouponNotifications();
                  setTamam(
                    metin.kuyrukCalistirildi(tamSayi(rapor.sent), tamSayi(rapor.abandoned), tamSayi(rapor.failed)),
                  );
                  await yukle();
                } catch (caught) {
                  setHata(hataMetni(caught, metin.hataBildirim));
                }
              }}
            >
              {metin.kuyruguCalistir}
            </button>
          ) : null}

          {/*
            Pes edilmiş satırlar. Yukarıdaki "Başarısız" sayacı bunları sayıyordu ama hiçbir yerde
            göstermiyordu; bir kuyruğun tıkandığını görmenin tek yolu buydu ve o yol yoktu.
          */}
          <div data-testid="coupon-notifications-abandoned">
            <h4>{metin.pesEdilenBasligi}</h4>
            <p className="muted">{metin.pesEdilenTarif}</p>
            {sayfa.abandoned.length === 0 ? (
              <p className="muted">{metin.pesEdilenYok}</p>
            ) : (
              <>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{metin.sutunRezervasyon}</th>
                        <th>{metin.sutunDeneme}</th>
                        <th>{metin.sutunSonDeneme}</th>
                        <th>{metin.sutunSonHata}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sayfa.abandoned.map((satir) => (
                        <tr key={satir.id} data-testid={`coupon-notification-abandoned-${satir.id}`}>
                          <td>{satir.reservationId}</td>
                          <td>{tamSayi(satir.attempts)}</td>
                          <td>{satir.lastAttemptAt ? tarihSaat(satir.lastAttemptAt) : TANIMSIZ}</td>
                          <td>{satir.lastError ?? TANIMSIZ}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {kuyrukYazabilir ? (
                  <button
                    type="button"
                    data-testid="coupon-notifications-requeue"
                    onClick={async () => {
                      setHata(null);
                      try {
                        const sonuc = await api.requeueCouponNotifications();
                        setTamam(metin.kuyrugaGeriKonuldu(tamSayi(sonuc.requeued)));
                        await yukle();
                      } catch (caught) {
                        setHata(hataMetni(caught, metin.hataGeriKoyma));
                      }
                    }}
                  >
                    {metin.kuyrugaGeriKoy}
                  </button>
                ) : null}
              </>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}

/** Kupon akışı vakası §5: kupon listesi. */
/**
 * `kuponYazabilir` **`coupon:write`**, `finance:write` degil.
 *
 * Bu bolumun iki yazma yolu da (`POST /finance/coupons/:code/publish` ve bildirim kuyrugunun iki
 * dugmesi) `coupon:write` istiyor. Ikisi ayni rolde birlikte duruyor (FINANCE) ama ayni sey
 * degiller ve `finance:write`a bakmak ADMIN ile MANAGER'da yanlis cevap veriyordu.
 */
function KuponBolumu({ kuponYazabilir }: { kuponYazabilir: boolean }) {
  const { sayfa, hata, yukleniyor, durum, setDurum, setSayfaNo, yukle } = useFinanceLedger<CouponListPage>(loadCoupons, FINANS_METIN.kuponlar.hata);
  const [seciliKod, setSeciliKod] = useState<string | null>(null);
  const metin = FINANS_METIN.kuponlar;

  const butceYaz = (coupon: Coupon) =>
    coupon.budget.remaining === null ? metin.sinirsiz : metin.kalan(tamSayi(coupon.budget.remaining));

  return (
    <>
      <section className="content-section" data-testid="coupons" aria-labelledby="coupons-title">
        <div className="section-heading">
          <div>
            <span className="eyebrow">{metin.ustBaslik}</span>
            <h2 id="coupons-title">{metin.baslik}</h2>
            <p className="muted">{metin.tarif}</p>
          </div>
          <span className="count-badge">{yukleniyor || hata || !sayfa ? TANIMSIZ : F.count(sayfa.total)}</span>
        </div>

        {hata ? <div className="error callout" role="alert">{hata}</div> : null}

        <div className="toolbar">
          <label>
            {metin.sutunDurum}{' '}
            <select value={durum} onChange={(olay) => setDurum(olay.target.value)}>
              <option value="">{metin.tumDurumlar}</option>
              {KUPON_DURUMLARI.map((deger) => <option key={deger} value={deger}>{KUPON_DURUMU[deger]}</option>)}
            </select>
          </label>
        <button type="button" disabled={yukleniyor} onClick={() => void yukle()}>{F.refresh}</button>
</div>

        <div className="table-wrap">
          <table data-testid="coupons-table">
            <thead>
              <tr>
                <th>{metin.sutunKod}</th>
                <th>{metin.sutunDurum}</th>
                <th className="money">{metin.sutunIndirim}</th>
                <th>{metin.sutunBudget}</th>
                <th>{metin.sutunYayin}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {yukleniyor || hata || !sayfa ? <tr><td colSpan={6} className="muted" role="status">{yukleniyor ? F.loading : F.failed}</td></tr> : sayfa.items.length === 0 ? (
                <tr><td colSpan={6} className="muted">{metin.bos}</td></tr>
              ) : (
                sayfa?.items.map((coupon) => (
                  <tr key={coupon.financeCouponId} data-testid="coupon-row">
                    <td data-label={metin.sutunKod}><code>{coupon.code}</code></td>
                    <td data-label={metin.sutunDurum}>{karsilik(KUPON_DURUMU, coupon.status)}</td>
                    <td data-label={metin.sutunIndirim} className="money">{indirimYaz(coupon.discount)}</td>
                    <td data-label={metin.sutunBudget}>{butceYaz(coupon)}</td>
                    <td data-label={metin.sutunYayin}>{karsilik(YAYIN_DURUMU, coupon.publication.state)}</td>
                    <td><button type="button" onClick={() => setSeciliKod(coupon.code)}>{metin.detay}</button></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      {!hata ? <FinancePagination page={sayfa} busy={yukleniyor} onPage={setSayfaNo} /> : null}

        {seciliKod ? <FinanceDrawer title={seciliKod} onClose={() => setSeciliKod(null)}><KuponAyrinti code={seciliKod} canWrite={kuponYazabilir} onKapat={() => setSeciliKod(null)} /></FinanceDrawer> : null}
      </section>
      <details className="finance-disclosure" data-testid="coupon-notifications-disclosure"><summary>{FINANS_METIN.kuponlar.bildirimlerBasligi}</summary><KuponBildirimleri kuyrukYazabilir={kuponYazabilir} /></details>
    </>
  );
}

/** Şirketin GeziPuan borcu ve tek bir üyenin defterinin araması. */
function GeziPuanBolumu() {
  const [borc, setBorc] = useState<GezipuanLiability | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [musteriId, setMusteriId] = useState('');
  const [defter, setDefter] = useState<GezipuanLedger | null>(null);
  const [aramaHata, setAramaHata] = useState<string | null>(null);
  const metin = FINANS_METIN.gezipuan;

  useEffect(() => {
    void (async () => {
      setHata(null);
      try {
        setBorc(await api.gezipuanLiability());
      } catch (caught) {
        setHata(hataMetni(caught, metin.hata));
      }
    })();
  }, [metin.hata]);

  return (
    <section className="content-section" data-testid="gezipuan-liability" aria-labelledby="gezipuan-liability-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="gezipuan-liability-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
      </div>

      {hata ? <div className="error callout" role="alert">{hata}</div> : null}

      {borc ? (
        <div className="stat-grid" data-testid="gezipuan-liability-stats">
          <span>{metin.harcanabilirBorc}: <strong>{para(borc.outstandingMinor, borc.currency)}</strong></span>
          <span>{metin.beklemedeBorc}: <strong>{para(borc.pendingMinor, borc.currency)}</strong></span>
          <span>{metin.toplamBorc}: <strong>{para(borc.committedMinor, borc.currency)}</strong></span>
          <span>{metin.otuzGunIcinde}: <strong>{para(borc.expiringWithin30DaysMinor, borc.currency)}</strong></span>
          <span>{metin.tutulan}: <strong>{para(borc.heldMinor, borc.currency)}</strong></span>
          <span>{metin.bakiyesiOlanUye}: <strong>{tamSayi(borc.membersWithBalance)}</strong></span>
          <span>{metin.supurulmemis}: <strong>{tamSayi(borc.unsweptExpiredCredits)}</strong></span>
          <span>{metin.hesaplananAn}: <strong>{tarihSaat(borc.asOf)}</strong></span>
        </div>
      ) : null}

      <h3>{metin.aramaBasligi}</h3>
      <div className="toolbar">
        <label>
          {metin.musteriKimligi}{' '}
          <input value={musteriId} onChange={(olay) => setMusteriId(olay.target.value)} />
        </label>
        <button
          type="button"
          onClick={async () => {
            setAramaHata(null);
            try {
              setDefter(await api.gezipuanLedger(musteriId));
            } catch (caught) {
              setDefter(null);
              setAramaHata(hataMetni(caught, metin.aramaHata));
            }
          }}
        >
          {metin.getir}
        </button>
      </div>
      {aramaHata ? <div className="error callout" role="alert">{aramaHata}</div> : null}
      {defter ? (
        <div className="stat-row" data-testid="gezipuan-lookup-result">
          <span>{ORTAK.tutar}: <strong>{para(defter.spendableMinor, defter.currency)}</strong></span>
          <span>{metin.beklemedeBorc}: <strong>{para(defter.pendingMinor, defter.currency)}</strong></span>
          <span>{metin.tutulan}: <strong>{para(defter.heldMinor, defter.currency)}</strong></span>
        </div>
      ) : null}
    </section>
  );
}

const TEDARIKCI_ODEME_DURUMLARI = ['UNPAID', 'PAYMENT_ORDERED', 'PAID'] as const;
const KESILEN_FATURA_DURUMLARI = ['PENDING', 'ISSUED', 'FAILED'] as const;

/** Bir maliyet kaleminin ödeme ayrıntısı: emir formu, ödendi işaretleme, fatura yükleme. */
function TedarikciOdemesiAyrinti({
  costEntryId,
  canWrite,
  onDegisti,
  onKapat,
}: {
  costEntryId: string;
  canWrite: boolean;
  onDegisti: () => void;
  onKapat: () => void;
}) {
  const [detay, setDetay] = useState<SupplierPaymentDetail | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [tamam, setTamam] = useState<string | null>(null);
  const [odemeReferansi, setOdemeReferansi] = useState('');
  const [odemeNotu, setOdemeNotu] = useState('');
  const [odendiNotu, setOdendiNotu] = useState('');
  const [faturaAdi, setFaturaAdi] = useState('');
  const [faturaTuru, setFaturaTuru] = useState('application/pdf');
  const [faturaDosya, setFaturaDosya] = useState<File | null>(null);
  const metin = FINANS_METIN.tedarikciOdemesi;

  const yukle = useCallback(async () => {
    setHata(null);
    try {
      setDetay(await api.supplierPaymentDetail(costEntryId));
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    }
  }, [costEntryId, metin.hata]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  const isle = async (eylem: () => Promise<unknown>, basari: string) => {
    setHata(null);
    setTamam(null);
    try {
      await eylem();
      setTamam(basari);
      await yukle();
      onDegisti();
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    }
  };

  const cost = detay?.cost;

  return (
    <section className="content-section" data-testid="supplier-payment-detail" aria-labelledby="supplier-payment-detail-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="supplier-payment-detail-title">{metin.detay}</h2>
        </div>
        <button type="button" onClick={onKapat}>{metin.kapat}</button>
      </div>

      {hata ? <div className="error callout" role="alert">{hata}</div> : null}
      {tamam ? <div className="callout" role="status">{tamam}</div> : null}

      {cost ? (
        <>
          <div className="stat-row">
            <span>{metin.sutunDurum}: <strong data-testid="supplier-payment-status">{karsilik(TEDARIKCI_ODEME_DURUMU, cost.paymentStatus)}</strong></span>
            <span>{metin.sutunTedarikci}: <strong>{cost.supplierName}</strong></span>
            <span>{metin.sutunAciklama}: <strong>{cost.description}</strong></span>
            <span>{metin.sutunTutar}: <strong>{para(cost.amountMinor, cost.currencyCode)}</strong></span>
            <span>{metin.sutunSiparis}: <strong>{cost.orderCode ?? cost.externalOrderReference ?? TANIMSIZ}</strong></span>
          </div>

          {cost.payment ? (
            <p className="callout" role="note">
              {metin.odemeReferansi}: {cost.payment.reference}
              {cost.payment.paidAt ? ` — ${tarihSaat(cost.payment.paidAt)}` : ` (${tarihSaat(cost.payment.orderedAt)})`}
            </p>
          ) : (
            <p className="muted">{metin.odemeYok}</p>
          )}

          {canWrite && cost.paymentStatus === 'UNPAID' ? (
            <>
              <AlanSatiri>
                <Alan etiket={metin.odemeReferansi} zorunlu>
                  <input value={odemeReferansi} onChange={(olay) => setOdemeReferansi(olay.target.value)} />
                </Alan>
                <Alan etiket={metin.not}>
                  <input value={odemeNotu} onChange={(olay) => setOdemeNotu(olay.target.value)} />
                </Alan>
              </AlanSatiri>
              <button
                type="button"
                data-testid="supplier-payment-order"
                disabled={!odemeReferansi}
                onClick={() => void isle(
                  () => api.recordSupplierPaymentOrder(costEntryId, { reference: odemeReferansi, ...(odemeNotu ? { note: odemeNotu } : {}) }),
                  metin.odemeEmriGirildi,
                )}
              >
                {metin.odemeEmriGir}
              </button>
            </>
          ) : null}

          {canWrite && cost.paymentStatus === 'PAYMENT_ORDERED' ? (
            <>
              <AlanSatiri>
                <Alan etiket={metin.not}>
                  <input value={odendiNotu} onChange={(olay) => setOdendiNotu(olay.target.value)} />
                </Alan>
              </AlanSatiri>
              <button
                type="button"
                data-testid="supplier-payment-paid"
                onClick={() => void isle(() => api.markSupplierPaymentPaid(costEntryId, odendiNotu ? { note: odendiNotu } : {}), metin.odendiIsaretlendi)}
              >
                {metin.odendiIsaretle}
              </button>
            </>
          ) : null}

          {canWrite ? (
            <>
              <details className="finance-disclosure"><summary>{F.addInvoice}</summary>
                <Alan etiket={metin.faturaIcerik} zorunlu><FinanceFile value={faturaDosya} onChange={(file) => { setFaturaDosya(file); if (file) { setFaturaAdi(file.name); setFaturaTuru(file.type || 'application/pdf'); } }} /></Alan>
                <details><summary>{F.fileDetails}</summary><AlanSatiri>
                  <Alan etiket={metin.faturaAdi} zorunlu><input value={faturaAdi} onChange={(olay) => setFaturaAdi(olay.target.value)} /></Alan>
                  <Alan etiket={metin.faturaTuru} zorunlu><input value={faturaTuru} onChange={(olay) => setFaturaTuru(olay.target.value)} /></Alan>
                </AlanSatiri></details>
              <button
                type="button"
                data-testid="supplier-expense-invoice-upload"
                disabled={!faturaDosya}
                onClick={() => void isle(async () => {
                  if (!faturaDosya) throw new Error(metin.faturaIcerik);
                  const contentBase64 = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
                    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
                    reader.readAsDataURL(faturaDosya);
                  });
                  const result = await api.uploadSupplierExpenseInvoice(costEntryId, { fileName: faturaAdi || faturaDosya.name, contentType: faturaTuru, contentBase64 });
                  setFaturaAdi('');
                  setFaturaDosya(null);
                  return result;
                }, metin.faturaYuklendi)}
              >
                {metin.faturaYukle}
              </button></details>
            </>
          ) : null}

          <h3>{metin.faturalarBasligi}</h3>
          {detay.invoices.length === 0 ? (
            <p className="muted">{metin.faturaYok}</p>
          ) : (
            <ul>
              {detay.invoices.map((invoice) => (
                <li key={invoice.id}>
                  {invoice.fileName} · {tarihSaat(invoice.uploadedAt)}{' '}
                  <button type="button" onClick={() => void api.downloadSupplierExpenseInvoice(invoice.id, invoice.fileName)}>{metin.faturaIndir}</button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}
    </section>
  );
}

/** Vaka madde 20: ödeme bekleyen (COMMITTED) tedarikçi maliyetlerinin listesi. */
function TedarikciOdemesiBolumu({ canWrite }: { canWrite: boolean }) {
  const { sayfa, hata, yukleniyor, durum, setDurum, setSayfaNo, yukle } = useFinanceLedger<SupplierPayableListPage>(loadSuppliers, FINANS_METIN.tedarikciOdemesi.hata);
  const [seciliId, setSeciliId] = useState<string | null>(null);
  const metin = FINANS_METIN.tedarikciOdemesi;

  return (
    <section className="content-section" data-testid="supplier-payments" aria-labelledby="supplier-payments-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="supplier-payments-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <span className="count-badge">{yukleniyor || hata || !sayfa ? TANIMSIZ : F.count(sayfa.total)}</span>
      </div>

      {hata ? <div className="error callout" role="alert">{hata}</div> : null}

      <div className="toolbar">
        <label>
          {metin.sutunDurum}{' '}
          <select value={durum} onChange={(olay) => setDurum(olay.target.value)}>
            <option value="">{metin.tumDurumlar}</option>
            {TEDARIKCI_ODEME_DURUMLARI.map((deger) => <option key={deger} value={deger}>{TEDARIKCI_ODEME_DURUMU[deger]}</option>)}
          </select>
        </label>
      <button type="button" disabled={yukleniyor} onClick={() => void yukle()}>{F.refresh}</button>
</div>

      <div className="table-wrap">
        <table data-testid="supplier-payments-table">
          <thead>
            <tr>
              <th>{metin.sutunTedarikci}</th>
              <th>{metin.sutunAciklama}</th>
              <th>{metin.sutunSiparis}</th>
              <th className="money">{metin.sutunTutar}</th>
              <th>{metin.sutunDurum}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {yukleniyor || hata || !sayfa ? <tr><td colSpan={6} className="muted" role="status">{yukleniyor ? F.loading : F.failed}</td></tr> : sayfa.items.length === 0 ? (
              <tr><td colSpan={6} className="muted">{metin.bos}</td></tr>
            ) : (
              sayfa?.items.map((cost) => (
                <tr key={cost.costEntryId} data-testid="supplier-payment-row">
                  <td data-label={metin.sutunTedarikci}>{cost.supplierName}</td>
                  <td data-label={metin.sutunAciklama}>{cost.description}</td>
                  <td data-label={metin.sutunSiparis}>{cost.orderCode ?? cost.externalOrderReference ?? TANIMSIZ}</td>
                  <td data-label={metin.sutunTutar} className="money">{para(cost.amountMinor, cost.currencyCode)}</td>
                  <td data-label={metin.sutunDurum}>{karsilik(TEDARIKCI_ODEME_DURUMU, cost.paymentStatus)}</td>
                  <td><button type="button" onClick={() => setSeciliId(cost.costEntryId)}>{metin.detay}</button></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!hata ? <FinancePagination page={sayfa} busy={yukleniyor} onPage={setSayfaNo} /> : null}

      {seciliId ? (
        <FinanceDrawer title={metin.detay} onClose={() => setSeciliId(null)}><TedarikciOdemesiAyrinti costEntryId={seciliId} canWrite={canWrite} onDegisti={() => void yukle()} onKapat={() => setSeciliId(null)} /></FinanceDrawer>
      ) : null}
    </section>
  );
}

/** Vaka madde 19: Finans'ın kesilen (PayPartner üstünden müşteriye çıkan) faturaları görmesi. Salt okunur. */
/**
 * Kesilecek fatura kuyruğu (Ürün Sahibi kararı 04.10.2026). Ödenmiş web siparişi burada görünür;
 * Finans önce her kalemin alış faturasını işler, sonra satış faturasını keser. Hesap sunucuda
 * (`sales-invoice.vat.ts`); bu ekran yalnız gösterir ve girdiyi toplar.
 */
function KesilecekFaturaBolumu({ canWrite }: { canWrite: boolean }) {
  const metin = FINANS_METIN.kesilecekFatura;
  const [kesilenler, setKesilenler] = useState(false);
  const yukleyici = useCallback(
    (page: number) => api.invoiceQueue({ page, pageSize: 20, ...(kesilenler ? { includeIssued: true } : {}) }),
    [kesilenler],
  );
  const { sayfa, hata, yukleniyor, setSayfaNo, yukle } = useFinanceLedger<InvoiceQueuePage>(yukleyici, metin.hata);
  const [secili, setSecili] = useState<string | null>(null);

  return (
    <section className="content-section" data-testid="invoice-queue" aria-labelledby="invoice-queue-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="invoice-queue-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <span className="count-badge">{yukleniyor || hata || !sayfa ? TANIMSIZ : F.count(sayfa.total)}</span>
      </div>
      {hata ? <div className="error callout" role="alert">{hata}</div> : null}
      <div className="toolbar">
        <label>
          <input type="checkbox" checked={kesilenler} onChange={(olay) => setKesilenler(olay.target.checked)} /> {metin.kesilenleriGoster}
        </label>
        <button type="button" disabled={yukleniyor} onClick={() => void yukle()}>{F.refresh}</button>
      </div>
      <div className="table-wrap">
        <table data-testid="invoice-queue-table">
          <thead>
            <tr>
              <th>{metin.sutunSiparis}</th>
              <th>{metin.sutunMusteri}</th>
              <th>{metin.sutunOdeme}</th>
              <th>{metin.sutunAlis}</th>
              <th className="money">{metin.sutunTutar}</th>
              <th className="money">{metin.sutunKdv}</th>
              <th>{metin.sutunDurum}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {yukleniyor || hata || !sayfa ? <tr><td colSpan={8} className="muted" role="status">{yukleniyor ? F.loading : F.failed}</td></tr> : sayfa.items.length === 0 ? (
              <tr><td colSpan={8} className="muted">{metin.bos}</td></tr>
            ) : sayfa.items.map((satir) => (
              <tr key={satir.reference} data-testid="invoice-queue-row">
                <td data-label={metin.sutunSiparis}>{satir.reference}</td>
                <td data-label={metin.sutunMusteri}>{satir.customerName}</td>
                <td data-label={metin.sutunOdeme}>{para(satir.totalMinor, satir.currencyCode)}<small>{satir.paidAt ? tarihSaat(satir.paidAt) : TANIMSIZ}</small></td>
                <td data-label={metin.sutunAlis} data-testid="invoice-queue-purchase-state">{metin.alisSayaci(satir.linesWithPurchase, satir.lineCount)}<small>{satir.purchaseRecorded ? metin.alisIslendi : metin.alisIslenmedi}</small></td>
                <td data-label={metin.sutunTutar} className="money">{satir.payableAedMinor === null ? TANIMSIZ : para(satir.payableAedMinor, 'AED')}</td>
                <td data-label={metin.sutunKdv} className="money">{satir.vatAedMinor === null ? TANIMSIZ : para(satir.vatAedMinor, 'AED')}</td>
                <td data-label={metin.sutunDurum}>
                  <span className="finance-badge" data-kind={satir.queueState}>{karsilik(KESILECEK_FATURA_DURUMU, satir.queueState)}</span>
                  {satir.missing.length ? <small>{satir.missing.map((anahtar) => karsilik(FATURA_KAPISI, anahtar)).join(' · ')}</small> : null}
                </td>
                <td><button type="button" data-testid={`invoice-queue-open-${satir.reference}`} onClick={() => setSecili(satir.reference)}>{metin.ac}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!hata ? <FinancePagination page={sayfa} busy={yukleniyor} onPage={setSayfaNo} /> : null}
      {secili ? (
        <FinanceDrawer title={secili} onClose={() => { setSecili(null); void yukle(); }}>
          <KesilecekFaturaDetayi reference={secili} canWrite={canWrite} />
        </FinanceDrawer>
      ) : null}
    </section>
  );
}

function dosyaBase64(dosya: File, hata: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(new Error(hata));
    reader.readAsDataURL(dosya);
  });
}

/** "5" -> 500 baz puan; boş -> undefined; geçersiz -> hata. */
function oranBaz(deger: string, hata: string): number | undefined {
  if (!deger.trim()) return undefined;
  const sayi = Number(deger.replace(',', '.'));
  if (!Number.isFinite(sayi) || sayi < 0 || sayi > 100) throw new Error(hata);
  return Math.round(sayi * 100);
}

function BelgeBaglantisi({ belgeId, ad }: { belgeId: string | null; ad: string }) {
  const metin = FINANS_METIN.kesilecekFatura;
  const [hata, setHata] = useState<string | null>(null);
  if (!belgeId) return <span className="muted">{metin.belgeYok}</span>;
  return (
    <>
      <button type="button" data-testid="invoice-document-download" onClick={() => { setHata(null); api.downloadInvoiceDocument(belgeId, ad).catch((caught: unknown) => setHata(hataMetni(caught, metin.hata))); }}>{metin.belgeAc}</button>
      {hata ? <small className="error" role="alert"> {hata}</small> : null}
    </>
  );
}

/** Alış faturası düzeltme formu (satır içi). Kalem ve tür sabit; gönderilmeyen alan değişmez. */
function AlisDuzeltme({ reference, satir, onDone }: { reference: string; satir: PurchaseInvoiceRow; onDone: () => void }) {
  const metin = FINANS_METIN.kesilecekFatura;
  const [f, setF] = useState({
    no: satir.supplierInvoiceNumber,
    tarih: satir.invoiceDate.slice(0, 10),
    tutar: tamSayiOndaliga(satir.amountMinor, 2),
    oran: satir.vatRateBps === null || satir.vatRateBps === undefined ? '' : String(satir.vatRateBps / 100),
    girdiKdv: tamSayiOndaliga(satir.inputVatAedMinor ?? 0, 2),
    not: satir.note ?? '',
  });
  const [hata, setHata] = useState<string | null>(null);
  return (
    <form className="finance-purchase-form" data-testid="purchase-edit-form" onSubmit={(olay) => {
      olay.preventDefault();
      setHata(null);
      void (async () => {
        try {
          const oran = oranBaz(f.oran, metin.gecersizTutar);
          await api.updatePurchaseInvoice(reference, satir.id, {
            supplierInvoiceNumber: f.no.trim(),
            invoiceDate: f.tarih,
            amountMinor: ondalikTamSayiya(f.tutar, 2, metin.gecersizTutar),
            inputVatAedMinor: ondalikTamSayiya(f.girdiKdv || '0', 2, metin.gecersizTutar),
            ...(oran === undefined ? {} : { vatRateBps: oran }),
            note: f.not.trim(),
          });
          onDone();
        } catch (caught) { setHata(caught instanceof Error ? hataCumlesi(caught) : metin.hata); }
      })();
    }}>
      {hata ? <div className="error callout" role="alert">{hata}</div> : null}
      <AlanSatiri>
        <Alan etiket={metin.alisNo} zorunlu><input value={f.no} maxLength={60} onChange={(olay) => setF({ ...f, no: olay.target.value })} /></Alan>
        <Alan etiket={metin.alisTarihi} zorunlu><input type="date" value={f.tarih} onChange={(olay) => setF({ ...f, tarih: olay.target.value })} /></Alan>
        <Alan etiket={`${metin.alisTutari} (${satir.currencyCode})`} zorunlu><input value={f.tutar} inputMode="decimal" onChange={(olay) => setF({ ...f, tutar: olay.target.value })} /></Alan>
      </AlanSatiri>
      <AlanSatiri>
        <Alan etiket={metin.girdiKdv}><input value={f.girdiKdv} inputMode="decimal" onChange={(olay) => setF({ ...f, girdiKdv: olay.target.value })} /></Alan>
        <Alan etiket={metin.kdvOrani}><input value={f.oran} inputMode="decimal" onChange={(olay) => setF({ ...f, oran: olay.target.value })} /></Alan>
        <Alan etiket={metin.not}><input value={f.not} maxLength={500} onChange={(olay) => setF({ ...f, not: olay.target.value })} /></Alan>
      </AlanSatiri>
      <button type="submit" data-testid="purchase-edit-save">{metin.kaydet}</button>{' '}
      <button type="button" onClick={onDone}>{metin.vazgec}</button>
    </form>
  );
}

function bugun(): string {
  return new Date().toISOString().slice(0, 10);
}

function KesilecekFaturaDetayi({ reference, canWrite }: { reference: string; canWrite: boolean }) {
  const metin = FINANS_METIN.kesilecekFatura;
  const [detay, setDetay] = useState<InvoiceQueueDetail | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [mesaj, setMesaj] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const [alis, setAlis] = useState({ itemReference: '', costKind: 'SUPPLIER_SERVICE', supplierName: '', supplierInvoiceNumber: '', invoiceDate: bugun(), currencyCode: 'AED', tutar: '', kur: '', girdiKdv: '', oran: '', note: '' });
  const [alisDosya, setAlisDosya] = useState<File | null>(null);
  const [duzenlenen, setDuzenlenen] = useState<string | null>(null);
  const [satis, setSatis] = useState({ no: '', tarih: bugun(), sirket: 'AE', para: 'AED', toplam: '', kdv: '', oran: '5', kur: '', url: '', note: '' });
  const [satisDosya, setSatisDosya] = useState<File | null>(null);
  const [satisDuzenle, setSatisDuzenle] = useState(false);
  const [kur, setKur] = useState({ deger: '', source: 'NOMOD_SETTLEMENT', rateDate: bugun() });

  const yukle = useCallback(async () => {
    try { setDetay(await api.invoiceQueueDetail(reference)); setHata(null); }
    catch (caught) { setHata(hataMetni(caught, metin.hata)); }
  }, [reference, metin.hata]);
  useEffect(() => { void yukle(); }, [yukle]);

  const calistir = async (is: () => Promise<string | null>) => {
    setMesgul(true); setHata(null); setMesaj(null);
    try { setMesaj(await is()); await yukle(); }
    catch (caught) { setHata(caught instanceof Error ? hataCumlesi(caught) : metin.hata); }
    finally { setMesgul(false); }
  };

  if (!detay) return hata ? <div className="error callout" role="alert">{hata}</div> : <p className="muted" role="status">{F.loading}</p>;
  const kesildi = detay.salesInvoice?.state === 'ISSUED';

  return (
    <div className="finance-invoice-queue-detail" data-testid="invoice-queue-detail">
      {hata ? <div className="error callout" role="alert">{hata}</div> : null}
      {mesaj ? <div className="callout" role="status">{mesaj}</div> : null}
      <p>
        <span className="finance-badge" data-kind={detay.queueState}>{karsilik(KESILECEK_FATURA_DURUMU, detay.queueState)}</span>{' '}
        {para(detay.totalMinor, detay.currencyCode)} · {detay.customerName}
        {detay.proformaNumber ? <> · {metin.proforma} {detay.proformaNumber}</> : null}
      </p>

      <h3>{metin.faturaKimligi}</h3>
      <p data-testid="invoice-queue-identity">
        {metin.kimlikKaynagi(karsilik(KIMLIK_KAYNAGI, detay.billingSource))}
        {detay.billingIdentity === null ? <> · {metin.kimlikYok}</> : detay.billingIdentity.visible ? (
          <> · {[detay.billingIdentity.nationalId ?? detay.billingIdentity.passportNumber, detay.billingIdentity.phone, detay.billingIdentity.countryCode, detay.billingIdentity.address].filter(Boolean).join(' · ')}</>
        ) : <> · {metin.kimlikGizli}</>}
      </p>

      <h3>{metin.kapilar}</h3>
      <ul data-testid="invoice-queue-gates">
        {detay.gates.map((kapi) => (
          <li key={kapi.key} data-met={kapi.met ? 'evet' : 'hayir'}>
            <strong>{karsilik(FATURA_KAPISI, kapi.key)}</strong>: {kapi.detail}
          </li>
        ))}
        {detay.gates.length === 0 ? <li>{metin.eksikYok}</li> : null}
      </ul>

      <h3>{metin.kurBaslik}</h3>
      {detay.currencyCode === 'AED' ? <p className="muted">{metin.kurGerekmez}</p> : detay.invoiceFx ? (
        <p>{metin.kurDonduruldu(tamSayiOndaliga(detay.invoiceFx.rateMicros, 6), detay.invoiceFx.source ? karsilik(KUR_KAYNAGI, detay.invoiceFx.source) : TANIMSIZ)}</p>
      ) : canWrite ? (
        <AlanSatiri>
          <Alan etiket={metin.kurDegeri} zorunlu><input value={kur.deger} inputMode="decimal" onChange={(olay) => setKur({ ...kur, deger: olay.target.value })} /></Alan>
          <Alan etiket={metin.kurKaynagi}><select value={kur.source} onChange={(olay) => setKur({ ...kur, source: olay.target.value })}>{Object.entries(KUR_KAYNAGI).map(([kod, ad]) => <option key={kod} value={kod}>{ad}</option>)}</select></Alan>
          <Alan etiket={metin.kurTarihi}><input type="date" value={kur.rateDate} onChange={(olay) => setKur({ ...kur, rateDate: olay.target.value })} /></Alan>
          <button type="button" disabled={mesgul} onClick={() => void calistir(async () => {
            const rateMicros = ondalikTamSayiya(kur.deger, 6, metin.gecersizKur);
            await api.freezeWebOrderFx(reference, { rateMicros, source: kur.source, rateDate: kur.rateDate });
            return null;
          })}>{metin.kurDondur}</button>
        </AlanSatiri>
      ) : null}

      <h3>{metin.kalemler}</h3>
      {detay.lines.map((kalem) => (
        <div key={kalem.itemReference} className="finance-invoice-line" data-testid="invoice-queue-line">
          <p><strong>{kalem.title ?? kalem.productId}</strong> · {kalem.itemReference} · {tamSayi(kalem.quantity)} × {para(kalem.unitAmountMinor, detay.currencyCode)}</p>
          {kalem.purchaseInvoices.length === 0 ? <p className="muted">{metin.alisYok}</p> : (
            <ul>
              {kalem.purchaseInvoices.map((alisFaturasi) => (
                <li key={alisFaturasi.id}>
                  {karsilik(ALIS_TURU, alisFaturasi.costKind)} · {alisFaturasi.supplierName} · {alisFaturasi.supplierInvoiceNumber} · {para(alisFaturasi.amountMinor, alisFaturasi.currencyCode)} ({para(alisFaturasi.amountAedMinor, 'AED')})
                  {alisFaturasi.vatRateBps === null || alisFaturasi.vatRateBps === undefined ? null : <> · {metin.kdvOrani}: {tamSayiOndaliga(alisFaturasi.vatRateBps, 2)}</>}
                  {' · '}<BelgeBaglantisi belgeId={alisFaturasi.documentId} ad={`alis-${alisFaturasi.supplierInvoiceNumber}.pdf`} />
                  {alisFaturasi.voidedAt ? <> · {metin.iptalEdildi}: {alisFaturasi.voidReason}</> : canWrite && (!kesildi || detay.salesInvoice?.source === 'MANUAL') ? (
                    <> <button type="button" data-testid="purchase-edit" onClick={() => setDuzenlenen(duzenlenen === alisFaturasi.id ? null : alisFaturasi.id)}>{metin.duzelt}</button>{' '}<button type="button" disabled={mesgul} onClick={() => {
                      const neden = window.prompt(metin.iptalNedeni);
                      if (neden && neden.trim()) void calistir(async () => { await api.voidPurchaseInvoice(reference, alisFaturasi.id, neden.trim()); return null; });
                    }}>{metin.iptal}</button></>
                  ) : null}
                  {duzenlenen === alisFaturasi.id ? <AlisDuzeltme reference={reference} satir={alisFaturasi} onDone={() => { setDuzenlenen(null); void yukle(); }} /> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}

      {canWrite && (!kesildi || detay.salesInvoice?.source === 'MANUAL') ? (
        <form className="finance-purchase-form" data-testid="purchase-form" onSubmit={(olay) => {
          olay.preventDefault();
          void calistir(async () => {
            if (!alis.itemReference || !alis.supplierName.trim() || !alis.supplierInvoiceNumber.trim()) throw new Error(metin.eksikAlan);
            const amountMinor = ondalikTamSayiya(alis.tutar, 2, metin.gecersizTutar);
            await api.recordPurchaseInvoice(reference, {
              itemReference: alis.itemReference,
              costKind: alis.costKind as 'SUPPLIER_SERVICE' | 'GOVERNMENT_FEE',
              supplierName: alis.supplierName.trim(),
              supplierInvoiceNumber: alis.supplierInvoiceNumber.trim(),
              invoiceDate: alis.invoiceDate,
              currencyCode: alis.currencyCode,
              amountMinor,
              ...(alis.currencyCode === 'AED' ? {} : { fxRateMicros: ondalikTamSayiya(alis.kur, 6, metin.gecersizKur) }),
              ...(alis.girdiKdv.trim() ? { inputVatAedMinor: ondalikTamSayiya(alis.girdiKdv, 2, metin.gecersizTutar) } : {}),
              ...(oranBaz(alis.oran, metin.gecersizTutar) === undefined ? {} : { vatRateBps: oranBaz(alis.oran, metin.gecersizTutar)! }),
              ...(alis.note.trim() ? { note: alis.note.trim() } : {}),
            });
            if (alisDosya) {
              // Belge, alış faturası kaydedildikten sonra bağlanır; kayıt belgeye bağlı değil.
              const kayit = await api.invoiceQueueDetail(reference);
              const yeni = kayit.lines.flatMap((kalem) => kalem.purchaseInvoices).find((satir) => satir.itemReference === alis.itemReference && satir.supplierInvoiceNumber === alis.supplierInvoiceNumber.trim() && !satir.voidedAt);
              if (yeni) await api.attachInvoiceDocument('purchase', yeni.id, { fileName: alisDosya.name, contentType: alisDosya.type || 'application/pdf', contentBase64: await dosyaBase64(alisDosya, metin.belgeOkunamadi) });
            }
            setAlis({ ...alis, supplierInvoiceNumber: '', tutar: '', kur: '', girdiKdv: '', oran: '', note: '' });
            setAlisDosya(null);
            return null;
          });
        }}>
          <h3>{metin.alisEkle}</h3>
          <AlanSatiri>
            <Alan etiket={metin.kalem} zorunlu><select value={alis.itemReference} onChange={(olay) => setAlis({ ...alis, itemReference: olay.target.value })}><option value="">{TANIMSIZ}</option>{detay.lines.map((kalem) => <option key={kalem.itemReference} value={kalem.itemReference}>{kalem.title ?? kalem.itemReference}</option>)}</select></Alan>
            <Alan etiket={metin.alisTuru} zorunlu><select value={alis.costKind} onChange={(olay) => setAlis({ ...alis, costKind: olay.target.value })}>{Object.entries(ALIS_TURU).map(([kod, ad]) => <option key={kod} value={kod}>{ad}</option>)}</select></Alan>
          </AlanSatiri>
          <AlanSatiri>
            <Alan etiket={metin.tedarikci} zorunlu><input value={alis.supplierName} maxLength={120} onChange={(olay) => setAlis({ ...alis, supplierName: olay.target.value })} /></Alan>
            <Alan etiket={metin.alisNo} zorunlu><input value={alis.supplierInvoiceNumber} maxLength={60} onChange={(olay) => setAlis({ ...alis, supplierInvoiceNumber: olay.target.value })} /></Alan>
            <Alan etiket={metin.alisTarihi} zorunlu><input type="date" value={alis.invoiceDate} onChange={(olay) => setAlis({ ...alis, invoiceDate: olay.target.value })} /></Alan>
          </AlanSatiri>
          <AlanSatiri>
            <Alan etiket={metin.paraBirimi}><select value={alis.currencyCode} onChange={(olay) => setAlis({ ...alis, currencyCode: olay.target.value })}>{['AED', 'EUR', 'USD', 'TRY'].map((kod) => <option key={kod} value={kod}>{kod}</option>)}</select></Alan>
            <Alan etiket={metin.alisTutari} zorunlu><input value={alis.tutar} inputMode="decimal" onChange={(olay) => setAlis({ ...alis, tutar: olay.target.value })} /></Alan>
            {alis.currencyCode === 'AED' ? null : <Alan etiket={metin.alisKuru} zorunlu><input value={alis.kur} inputMode="decimal" onChange={(olay) => setAlis({ ...alis, kur: olay.target.value })} /></Alan>}
            <Alan etiket={metin.girdiKdv}><input value={alis.girdiKdv} inputMode="decimal" onChange={(olay) => setAlis({ ...alis, girdiKdv: olay.target.value })} /></Alan>
            <Alan etiket={metin.kdvOrani}><input value={alis.oran} inputMode="decimal" data-testid="purchase-vat-rate" onChange={(olay) => setAlis({ ...alis, oran: olay.target.value })} /></Alan>
          </AlanSatiri>
          <Alan etiket={metin.belge}><FinanceFile value={alisDosya} onChange={setAlisDosya} /></Alan>
          <Alan etiket={metin.not}><input value={alis.note} maxLength={500} onChange={(olay) => setAlis({ ...alis, note: olay.target.value })} /></Alan>
          <button type="submit" disabled={mesgul} data-testid="invoice-queue-purchase-save">{metin.kaydet}</button>
        </form>
      ) : null}

      <h3>{metin.onizleme}</h3>
      {(() => {
        const ozet = detay.salesInvoice?.state === 'ISSUED' ? detay.salesInvoice : detay.preview;
        if (!ozet) return <p className="muted">{metin.onizlemeYok}</p>;
        return (
          <div className="table-wrap">
            <table data-testid="invoice-queue-preview">
              <thead><tr><th>{metin.satirTuru}</th><th>{metin.aciklama}</th><th className="money">{metin.tutar}</th><th className="money">{metin.kdv}</th></tr></thead>
              <tbody>
                {ozet.lines.map((satir, sira) => (
                  <tr key={`${satir.kind}-${String(sira)}`}>
                    <td>{karsilik(FATURA_SATIR_TURU, satir.kind)}</td>
                    <td>{satir.description}</td>
                    <td className="money">{para(satir.amountMinor, 'AED')}</td>
                    <td className="money">{para(satir.vatMinor, 'AED')}</td>
                  </tr>
                ))}
                <tr><td colSpan={2}><strong>{metin.odenecek}</strong></td><td className="money"><strong>{para(ozet.payableMinor, 'AED')}</strong></td><td className="money"><strong>{para(ozet.vatMinor, 'AED')}</strong></td></tr>
              </tbody>
            </table>
          </div>
        );
      })()}

      {detay.salesInvoice?.lastError && !kesildi ? <p className="error">{metin.sonHata}: {detay.salesInvoice.lastError}</p> : null}
      {kesildi && detay.salesInvoice ? (
        <div data-testid="invoice-queue-issued">
          <h3>{metin.satisKayitliBaslik}</h3>
          <p>
            {metin.kesildi(detay.salesInvoice.invoiceNumber ?? TANIMSIZ)}{detay.salesInvoice.alreadyExisted ? <> · {metin.mukerrer}</> : null}
            {' · '}{detay.salesInvoice.sellerEntity} · {detay.salesInvoice.source === 'MANUAL' ? metin.satisKaynakElle : metin.satisKaynakOtomasyon}
            {' · '}{para(detay.salesInvoice.payableMinor, detay.salesInvoice.currencyCode)} · {metin.kdv} {para(detay.salesInvoice.vatMinor, detay.salesInvoice.currencyCode)}
          </p>
          <p>
            <BelgeBaglantisi belgeId={detay.salesInvoice.documentId} ad={`satis-${detay.salesInvoice.invoiceNumber ?? 'fatura'}.pdf`} />
            {detay.salesInvoice.externalUrl ? <> · <a href={detay.salesInvoice.externalUrl} target="_blank" rel="noreferrer noopener">{metin.payPartnerBaglanti}</a></> : null}
          </p>
          {canWrite && detay.salesInvoice.source === 'MANUAL' ? (
            <>
              <label className="muted"><input type="file" accept="application/pdf,image/png,image/jpeg" data-testid="sales-document-input" onChange={(olay) => {
                const dosya = olay.target.files?.[0];
                if (dosya) void calistir(async () => { await api.attachInvoiceDocument('sales', detay.salesInvoice!.id, { fileName: dosya.name, contentType: dosya.type || 'application/pdf', contentBase64: await dosyaBase64(dosya, metin.belgeOkunamadi) }); return null; });
              }} /> {metin.belge}</label>{' '}
              <button type="button" data-testid="sales-edit" onClick={() => {
                const k = detay.salesInvoice!;
                setSatis({ no: k.invoiceNumber ?? '', tarih: k.invoiceDate.slice(0, 10), sirket: k.sellerEntity, para: k.currencyCode, toplam: tamSayiOndaliga(k.payableMinor, 2), kdv: tamSayiOndaliga(k.vatMinor, 2), oran: k.vatRateBps === null ? '' : String(k.vatRateBps / 100), kur: k.fxRateMicros ? tamSayiOndaliga(k.fxRateMicros, 6) : '', url: k.externalUrl ?? '', note: k.note ?? '' });
                setSatisDuzenle(!satisDuzenle);
              }}>{metin.duzelt}</button>
            </>
          ) : null}
        </div>
      ) : null}
      {canWrite && (!kesildi || (satisDuzenle && detay.salesInvoice?.source === 'MANUAL')) ? (
        <form className="finance-purchase-form" data-testid="sales-record-form" onSubmit={(olay) => {
          olay.preventDefault();
          void calistir(async () => {
            if (!satis.no.trim() || !satis.toplam.trim()) throw new Error(metin.eksikAlan);
            const govde: SalesInvoiceRecordInput = {
              invoiceNumber: satis.no.trim(),
              invoiceDate: satis.tarih,
              sellerEntity: satis.sirket as 'AE' | 'TR',
              currencyCode: satis.para,
              payableMinor: ondalikTamSayiya(satis.toplam, 2, metin.gecersizTutar),
              vatMinor: ondalikTamSayiya(satis.kdv || '0', 2, metin.gecersizTutar),
              ...(oranBaz(satis.oran, metin.gecersizTutar) === undefined ? {} : { vatRateBps: oranBaz(satis.oran, metin.gecersizTutar)! }),
              ...(satis.para === 'AED' || !satis.kur.trim() ? {} : { fxRateMicros: ondalikTamSayiya(satis.kur, 6, metin.gecersizKur) }),
              ...(satis.url.trim() ? { externalUrl: satis.url.trim() } : {}),
              ...(satis.note.trim() ? { note: satis.note.trim() } : {}),
            };
            let mesajMetni: string;
            let faturaId: string;
            if (kesildi) {
              const sonuc = await api.updateSalesInvoiceRecord(reference, govde);
              faturaId = sonuc.invoice.id;
              mesajMetni = metin.duzeltildi;
              setSatisDuzenle(false);
            } else {
              const sonuc = await api.recordSalesInvoice(reference, govde);
              faturaId = sonuc.invoice.id;
              mesajMetni = sonuc.warnings.map((kod) => metin.uyariKodlari[kod] ?? kod).join(' ') || metin.satisKaydedildi(sonuc.invoice.invoiceNumber ?? TANIMSIZ);
            }
            if (satisDosya) {
              await api.attachInvoiceDocument('sales', faturaId, { fileName: satisDosya.name, contentType: satisDosya.type || 'application/pdf', contentBase64: await dosyaBase64(satisDosya, metin.belgeOkunamadi) });
              setSatisDosya(null);
            }
            return mesajMetni;
          });
        }}>
          <h3>{metin.satisKaydi}</h3>
          <p className="muted">{metin.tarif2}</p>
          {!detay.purchaseRecorded && !kesildi ? <div className="callout" role="status" data-testid="sales-purchase-warning">{metin.alisUyarisi}</div> : null}
          <AlanSatiri>
            <Alan etiket={metin.satisNo} zorunlu><input value={satis.no} maxLength={40} data-testid="sales-number" onChange={(olay) => setSatis({ ...satis, no: olay.target.value })} /></Alan>
            <Alan etiket={metin.satisTarihi} zorunlu><input type="date" value={satis.tarih} onChange={(olay) => setSatis({ ...satis, tarih: olay.target.value })} /></Alan>
            <Alan etiket={metin.saticiSirket} zorunlu><select value={satis.sirket} data-testid="sales-seller" onChange={(olay) => setSatis({ ...satis, sirket: olay.target.value })}><option value="AE">{metin.saticiAE}</option><option value="TR">{metin.saticiTR}</option></select></Alan>
          </AlanSatiri>
          <AlanSatiri>
            <Alan etiket={metin.paraBirimi}><select value={satis.para} onChange={(olay) => setSatis({ ...satis, para: olay.target.value })}>{['AED', 'EUR', 'USD', 'TRY'].map((kod) => <option key={kod} value={kod}>{kod}</option>)}</select></Alan>
            <Alan etiket={metin.satisToplam} zorunlu><input value={satis.toplam} inputMode="decimal" data-testid="sales-total" onChange={(olay) => setSatis({ ...satis, toplam: olay.target.value })} /></Alan>
            <Alan etiket={metin.satisKdv}><input value={satis.kdv} inputMode="decimal" data-testid="sales-vat" onChange={(olay) => setSatis({ ...satis, kdv: olay.target.value })} /></Alan>
            <Alan etiket={metin.kdvOrani}><input value={satis.oran} inputMode="decimal" onChange={(olay) => setSatis({ ...satis, oran: olay.target.value })} /></Alan>
            {satis.para === 'AED' || satis.para === 'USD' ? null : <Alan etiket={metin.satisKuru} zorunlu><input value={satis.kur} inputMode="decimal" onChange={(olay) => setSatis({ ...satis, kur: olay.target.value })} /></Alan>}
          </AlanSatiri>
          <Alan etiket={metin.baglanti}><input value={satis.url} maxLength={500} placeholder="https://" onChange={(olay) => setSatis({ ...satis, url: olay.target.value })} /></Alan>
          <Alan etiket={metin.belge}><FinanceFile value={satisDosya} onChange={setSatisDosya} /></Alan>
          <Alan etiket={metin.not}><input value={satis.note} maxLength={500} onChange={(olay) => setSatis({ ...satis, note: olay.target.value })} /></Alan>
          <button type="submit" className="primary" disabled={mesgul} data-testid="sales-record-save">{kesildi ? metin.kaydet : metin.satisKaydet}</button>
        </form>
      ) : null}
    </div>
  );
}

/** Bütün siparişlerin alış faturaları: arama, belge, siparişe geçiş. Ekleme sipariş kaleminden yapılır. */
function AlisFaturalariBolumu({ canWrite }: { canWrite: boolean }) {
  const metin = FINANS_METIN.alisFaturalari;
  const [arama, setArama] = useState('');
  const [iptaller, setIptaller] = useState(false);
  const yukleyici = useCallback(
    (page: number) => api.purchaseInvoices({ page, pageSize: 20, ...(arama.trim() ? { q: arama.trim() } : {}), ...(iptaller ? { includeVoided: true } : {}) }),
    [arama, iptaller],
  );
  const { sayfa, hata, yukleniyor, setSayfaNo, yukle } = useFinanceLedger<PurchaseInvoiceListPage>(yukleyici, metin.hata);
  const [secili, setSecili] = useState<string | null>(null);
  return (
    <section className="content-section" data-testid="purchase-invoices" aria-labelledby="purchase-invoices-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="purchase-invoices-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <span className="count-badge">{yukleniyor || hata || !sayfa ? TANIMSIZ : F.count(sayfa.total)}</span>
      </div>
      {hata ? <div className="error callout" role="alert">{hata}</div> : null}
      <div className="toolbar">
        <label>{metin.ara} <input type="search" value={arama} data-testid="purchase-invoices-search" onChange={(olay) => { setArama(olay.target.value); setSayfaNo(1); }} /></label>
        <label><input type="checkbox" checked={iptaller} onChange={(olay) => setIptaller(olay.target.checked)} /> {metin.iptalleriGoster}</label>
        <button type="button" disabled={yukleniyor} onClick={() => void yukle()}>{F.refresh}</button>
      </div>
      <div className="table-wrap">
        <table data-testid="purchase-invoices-table">
          <thead>
            <tr>
              <th>{metin.sutunTedarikci}</th><th>{metin.sutunNo}</th><th>{metin.sutunTarih}</th>
              <th className="money">{metin.sutunTutar}</th><th className="money">{metin.sutunKdv}</th>
              <th>{metin.sutunSiparis}</th><th>{metin.sutunBelge}</th>
            </tr>
          </thead>
          <tbody>
            {yukleniyor || hata || !sayfa ? <tr><td colSpan={7} className="muted" role="status">{yukleniyor ? F.loading : F.failed}</td></tr> : sayfa.items.length === 0 ? (
              <tr><td colSpan={7} className="muted">{metin.bos}</td></tr>
            ) : sayfa.items.map((satir) => (
              <tr key={satir.id} data-testid="purchase-invoices-row">
                <td data-label={metin.sutunTedarikci}>{satir.supplierName}{satir.voidedAt ? <small>{metin.iptal}</small> : null}</td>
                <td data-label={metin.sutunNo}>{satir.supplierInvoiceNumber}</td>
                <td data-label={metin.sutunTarih}>{tarih(satir.invoiceDate)}</td>
                <td data-label={metin.sutunTutar} className="money">{para(satir.amountMinor, satir.currencyCode)}<small>{para(satir.amountAedMinor, 'AED')}</small></td>
                <td data-label={metin.sutunKdv} className="money">{satir.vatRateBps === null || satir.vatRateBps === undefined ? TANIMSIZ : `%${tamSayiOndaliga(satir.vatRateBps, 2)}`}</td>
                <td data-label={metin.sutunSiparis}><button type="button" onClick={() => setSecili(satir.orderReference)}>{satir.orderReference}</button></td>
                <td data-label={metin.sutunBelge}><BelgeBaglantisi belgeId={satir.documentId} ad={`alis-${satir.supplierInvoiceNumber}.pdf`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!hata ? <FinancePagination page={sayfa} busy={yukleniyor} onPage={setSayfaNo} /> : null}
      {secili ? (
        <FinanceDrawer title={secili} onClose={() => { setSecili(null); void yukle(); }}>
          <KesilecekFaturaDetayi reference={secili} canWrite={canWrite} />
        </FinanceDrawer>
      ) : null}
    </section>
  );
}

/** Sipariş başına alış, satış ve fark (KDV hariç). USD karşılığı AED sabit bağlı kurdan. */
function SiparisFarkiBolumu() {
  const metin = FINANS_METIN.siparisFarki;
  const yukleyici = useCallback((page: number) => api.invoiceMargin({ page, pageSize: 20 }), []);
  const { sayfa, hata, yukleniyor, setSayfaNo, yukle } = useFinanceLedger<InvoiceMarginPage>(yukleyici, metin.hata);
  return (
    <section className="content-section" data-testid="invoice-margin" aria-labelledby="invoice-margin-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="invoice-margin-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <span className="count-badge">{yukleniyor || hata || !sayfa ? TANIMSIZ : F.count(sayfa.total)}</span>
      </div>
      {hata ? <div className="error callout" role="alert">{hata}</div> : null}
      <div className="toolbar"><button type="button" disabled={yukleniyor} onClick={() => void yukle()}>{F.refresh}</button></div>
      <div className="table-wrap">
        <table data-testid="invoice-margin-table">
          <thead>
            <tr>
              <th>{metin.sutunSiparis}</th><th className="money">{metin.sutunAlis}</th><th className="money">{metin.sutunSatis}</th>
              <th className="money">{metin.sutunFark}</th><th className="money">{metin.sutunFarkUsd}</th><th className="money">{metin.sutunOran}</th>
            </tr>
          </thead>
          <tbody>
            {yukleniyor || hata || !sayfa ? <tr><td colSpan={6} className="muted" role="status">{yukleniyor ? F.loading : F.failed}</td></tr> : sayfa.items.length === 0 ? (
              <tr><td colSpan={6} className="muted">{metin.bos}</td></tr>
            ) : sayfa.items.map((satir) => (
              <tr key={satir.reference} data-testid="invoice-margin-row">
                <td data-label={metin.sutunSiparis}>{satir.reference}<small>{satir.customerName}</small></td>
                <td data-label={metin.sutunAlis} className="money">{satir.purchaseCount === 0 ? metin.alisYok : para(satir.purchaseNetAedMinor, 'AED')}</td>
                <td data-label={metin.sutunSatis} className="money">{satir.salesNetAedMinor === null ? metin.satisYok : para(satir.salesNetAedMinor, 'AED')}<small>{satir.salesInvoiceNumber ? `${satir.sellerEntity ?? ''} ${satir.salesInvoiceNumber}` : ''}</small></td>
                <td data-label={metin.sutunFark} className="money" data-testid="invoice-margin-aed">{satir.marginAedMinor === null ? TANIMSIZ : para(satir.marginAedMinor, 'AED')}</td>
                <td data-label={metin.sutunFarkUsd} className="money">{satir.marginUsdMinor === null ? TANIMSIZ : para(satir.marginUsdMinor, 'USD')}</td>
                <td data-label={metin.sutunOran} className="money">{satir.marginPercentBps === null ? TANIMSIZ : `%${tamSayiOndaliga(satir.marginPercentBps, 2)}`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!hata ? <FinancePagination page={sayfa} busy={yukleniyor} onPage={setSayfaNo} /> : null}
    </section>
  );
}

function KesilenFaturalarBolumu() {
  const { sayfa, hata, yukleniyor, durum, setDurum, setSayfaNo, yukle } = useFinanceLedger<OrderInvoiceListPage>(loadInvoices, FINANS_METIN.kesilenFaturalar.hata);
  const metin = FINANS_METIN.kesilenFaturalar;

  return (
    <section className="content-section" data-testid="order-invoices" aria-labelledby="order-invoices-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="order-invoices-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <span className="count-badge">{yukleniyor || hata || !sayfa ? TANIMSIZ : F.count(sayfa.total)}</span>
      </div>

      {hata ? <div className="error callout" role="alert">{hata}</div> : null}

      <div className="toolbar">
        <label>
          {metin.sutunDurum}{' '}
          <select value={durum} onChange={(olay) => setDurum(olay.target.value)}>
            <option value="">{metin.tumDurumlar}</option>
            {KESILEN_FATURA_DURUMLARI.map((deger) => <option key={deger} value={deger}>{KESILEN_FATURA_DURUMU[deger]}</option>)}
          </select>
        </label>
      <button type="button" disabled={yukleniyor} onClick={() => void yukle()}>{F.refresh}</button>
</div>

      <div className="table-wrap">
        <table data-testid="order-invoices-table">
          <thead>
            <tr>
              <th>{metin.sutunSiparis}</th>
              <th>{metin.sutunMusteri}</th>
              <th>{metin.sutunNumara}</th>
              <th className="money">{metin.sutunTutar}</th>
              <th>{metin.sutunDurum}</th>
              <th>{metin.sutunTarih}</th>
            </tr>
          </thead>
          <tbody>
            {yukleniyor || hata || !sayfa ? <tr><td colSpan={6} className="muted" role="status">{yukleniyor ? F.loading : F.failed}</td></tr> : sayfa.items.length === 0 ? (
              <tr><td colSpan={6} className="muted">{metin.bos}</td></tr>
            ) : (
              sayfa?.items.map((invoice) => (
                <tr key={invoice.id} data-testid="order-invoice-row">
                  <td data-label={metin.sutunSiparis}>{invoice.orderCode}</td>
                  <td data-label={metin.sutunMusteri}>{invoice.customerName ?? TANIMSIZ}</td>
                  <td data-label={metin.sutunNumara}>{invoice.invoiceNumber ?? TANIMSIZ}</td>
                  <td data-label={metin.sutunTutar} className="money">{para(invoice.amountMinor, invoice.currencyCode)}</td>
                  <td data-label={metin.sutunDurum}>
                    {karsilik(KESILEN_FATURA_DURUMU, invoice.state)}
                    {invoice.state === 'FAILED' && invoice.lastError ? ` — ${invoice.lastError}` : ''}
                  </td>
                  <td data-label={metin.sutunTarih}>{tarihSaat(invoice.requestedAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!hata ? <FinancePagination page={sayfa} busy={yukleniyor} onPage={setSayfaNo} /> : null}
    </section>
  );
}

/**
 * FR-20: marj kuralları.
 *
 * **Neden burada.** Kuralı yazma yetkisi (`margin_rule:write`) iki roldedir, Finans ve Müdür, ve
 * ikisi de bu sayfayı görüyor. Satış sayfasına konsaydı kuralı yazamayacak tek rolün ekranında
 * duran bir düğme olurdu; yapılandırma sayfası ise yalnız sistem yöneticisine açık ve o rol bu
 * yetkiyi tutmuyor. Kuralın taşıdığı marj yüzdesi FR-25 kapsamında iç veridir, yani ait olduğu
 * yer zaten para ekranı.
 *
 * Ölçülen kusur: kural motoru bütün fiyat önerisinin dayanağı ve veritabanındaki tek kuralı tohum
 * yazmıştı. Üç uç da 22.08'den beri açıktı; panelden ne yeni kural yazılabiliyordu ne de var olan
 * bir kural kapatılabiliyordu.
 *
 * Kural silinmiyor, **kapatılıyor**: uçta silme yok ve olmamalı — kapanan bir kuralın hangi
 * fiyatları ürettiği kaydın kendisinde duruyor.
 */
function MarjKurallariBolumu({ canReadFinance }: { canReadFinance: boolean }) {
  const [kurallar, setKurallar] = useState<MarginRule[] | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [tamam, setTamam] = useState<string | null>(null);
  const [ad, setAd] = useState('');
  const [oncelik, setOncelik] = useState('');
  const [tur, setTur] = useState('');
  const [destinasyon, setDestinasyon] = useState('');
  const [sezonBaslangic, setSezonBaslangic] = useState('');
  const [sezonBitis, setSezonBitis] = useState('');
  const [segment, setSegment] = useState('');
  const [altTutar, setAltTutar] = useState('');
  const [ustTutar, setUstTutar] = useState('');
  const [bantParaBirimi, setBantParaBirimi] = useState('');
  const [marjYuzde, setMarjYuzde] = useState('');
  const [marjSabit, setMarjSabit] = useState('');
  const metin = FINANS_METIN.marjKurallari;

  const yukle = useCallback(async () => {
    setHata(null);
    try {
      setKurallar(await api.marginRules());
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    }
  }, [metin.hata]);

  /**
   * Bu bölümün hata cümlesi `hataMetni` değil `hataCumlesi` (`Workflow.tsx`teki `describe`).
   *
   * Ölçüldü: ekranın kendi doğrulaması (ör. "en çok iki ondalık basamak") düz bir `Error` olarak
   * atılıyor ve `hataMetni` yalnız uç hatalarının metnini geçiriyordu — kullanıcı, yazdığı
   * yüzdenin neden kabul edilmediğini öğrenemiyor, yerine "marj kuralları okunamadı" okuyordu.
   * Genel bir cümle, adı konmuş bir reddin yerini tutmaz.
   */

  useEffect(() => {
    void yukle();
  }, [yukle]);

  const isle = async (eylem: () => Promise<unknown>, basari: string) => {
    setHata(null);
    setTamam(null);
    try {
      await eylem();
      setTamam(basari);
      await yukle();
    } catch (caught) {
      setHata(hataCumlesi(caught));
    }
  };

  /** Kuralın hangi duruma uyduğu, tek satırda. Boş alan "hepsi" demek ve bu ekranda yazılı. */
  const kapsamYazisi = (kural: MarginRule): string => {
    const parcalar: string[] = [];
    if (kural.itemType) parcalar.push(metin.kapsamTur(kalemTuru(kural.itemType)));
    if (kural.destination) parcalar.push(metin.kapsamDestinasyon(kural.destination));
    if (kural.seasonStartMonth !== null && kural.seasonEndMonth !== null) {
      parcalar.push(metin.kapsamSezon(tamSayi(kural.seasonStartMonth), tamSayi(kural.seasonEndMonth)));
    }
    if (kural.customerSegmentTag) parcalar.push(metin.kapsamEtiket(kural.customerSegmentTag));
    const alt = kural.minAmountMinor === null ? null : para(kural.minAmountMinor, kural.bandCurrencyCode);
    const ust = kural.maxAmountMinor === null ? null : para(kural.maxAmountMinor, kural.bandCurrencyCode);
    if (alt !== null && ust !== null) parcalar.push(metin.kapsamBant(alt, ust));
    else if (alt !== null) parcalar.push(metin.kapsamAltSinir(alt));
    else if (ust !== null) parcalar.push(metin.kapsamUstSinir(ust));
    return parcalar.length === 0 ? metin.kapsamHepsi : parcalar.join(' · ');
  };

  /**
   * FR-25: marj yüzdesi ve sabit marj tutarı iç veridir. Uç bu listeyi zaten dar bir izin
   * arkasında tutuyor, ama ekran ikinci bir kapı koyuyor — bu satırın çizilmesi finans okuma
   * yetkisine bağlı ve yetkinin olmaması gizlenmiş bir hücre değil, yazılı bir cümle üretiyor.
   */
  const marjYazisi = (kural: MarginRule): string => {
    if (!canReadFinance) return metin.marjGizli;
    const parcalar: string[] = [];
    if (kural.marginBps !== null) parcalar.push(metin.marjYuzde(tamSayiOndaliga(kural.marginBps, 2)));
    if (kural.marginFixedMinor !== null) {
      parcalar.push(metin.marjSabit(para(kural.marginFixedMinor, kural.bandCurrencyCode)));
    }
    return parcalar.length === 0 ? TANIMSIZ : parcalar.join(' · ');
  };

  return (
    <section className="content-section" data-testid="margin-rules" aria-labelledby="margin-rules-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="margin-rules-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <span className="count-badge">{tamSayi(kurallar?.length ?? 0)} {ORTAK.kayit}</span>
      </div>

      {hata ? <div className="error callout" role="alert" data-testid="margin-rules-error">{hata}</div> : null}
      {tamam ? <div className="callout callout-ok" role="status" data-testid="margin-rules-notice">{tamam}</div> : null}

      <div className="table-wrap">
        <table data-testid="margin-rules-table">
          <thead>
            <tr>
              <th>{metin.sutunAd}</th>
              <th>{metin.sutunOncelik}</th>
              <th>{metin.sutunKapsam}</th>
              <th>{metin.sutunMarj}</th>
              <th>{metin.sutunDurum}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(kurallar?.length ?? 0) === 0 ? (
              <tr><td colSpan={6} className="muted">{metin.bos}</td></tr>
            ) : (
              kurallar?.map((kural) => (
                <tr key={kural.id} data-testid="margin-rule-row">
                  <td data-label={metin.sutunAd}>{kural.name}</td>
                  <td data-label={metin.sutunOncelik}>{tamSayi(kural.priority)}</td>
                  <td data-label={metin.sutunKapsam}>{kapsamYazisi(kural)}</td>
                  <td data-label={metin.sutunMarj} data-testid={`margin-rule-margin-${kural.id}`}>{marjYazisi(kural)}</td>
                  <td data-label={metin.sutunDurum}>{kural.active ? metin.etkin : metin.pasif}</td>
                  <td>
                    <button
                      type="button"
                      className="ikincil"
                      data-testid={`margin-rule-toggle-${kural.id}`}
                      onClick={() => void isle(
                        () => api.setMarginRuleActive(kural.id, { active: !kural.active, expectedVersion: kural.version }),
                        kural.active ? metin.kapatildi(kural.name) : metin.acildi(kural.name),
                      )}
                    >
                      {kural.active ? metin.kapat : metin.ac}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <details className="finance-disclosure" data-testid="margin-rule-form"><summary>{metin.yeniBaslik}</summary>
      <AlanSatiri>
        <Alan etiket={metin.adEtiketi} ipucu={metin.adIpucu} zorunlu>
          <input data-testid="margin-rule-name" value={ad} maxLength={120} onChange={(olay) => setAd(olay.target.value)} />
        </Alan>
        <Alan etiket={metin.oncelikEtiketi} ipucu={metin.oncelikIpucu}>
          <input data-testid="margin-rule-priority" inputMode="numeric" value={oncelik} onChange={(olay) => setOncelik(olay.target.value)} />
        </Alan>
        <Alan etiket={metin.turEtiketi}>
          <select data-testid="margin-rule-item-type" value={tur} onChange={(olay) => setTur(olay.target.value)}>
            <option value="">{metin.turHepsi}</option>
            {Object.keys(KALEM_TURU).map((deger) => (
              <option key={deger} value={deger}>{kalemTuru(deger)}</option>
            ))}
          </select>
        </Alan>
        <Alan etiket={metin.destinasyonEtiketi} ipucu={metin.destinasyonIpucu}>
          <input data-testid="margin-rule-destination" value={destinasyon} maxLength={160} onChange={(olay) => setDestinasyon(olay.target.value)} />
        </Alan>
      </AlanSatiri>
      <AlanSatiri>
        <Alan etiket={metin.sezonBaslangicEtiketi} ipucu={metin.sezonIpucu}>
          <input data-testid="margin-rule-season-start" inputMode="numeric" value={sezonBaslangic} onChange={(olay) => setSezonBaslangic(olay.target.value)} />
        </Alan>
        <Alan etiket={metin.sezonBitisEtiketi} ipucu={metin.sezonIpucu}>
          <input data-testid="margin-rule-season-end" inputMode="numeric" value={sezonBitis} onChange={(olay) => setSezonBitis(olay.target.value)} />
        </Alan>
        <Alan etiket={metin.segmentEtiketi}>
          <input data-testid="margin-rule-segment" value={segment} maxLength={60} onChange={(olay) => setSegment(olay.target.value)} />
        </Alan>
      </AlanSatiri>
      <AlanSatiri>
        <Alan etiket={metin.altTutarEtiketi} ipucu={ORTAK.tutar}>
          <input data-testid="margin-rule-min" inputMode="decimal" value={altTutar} onChange={(olay) => setAltTutar(olay.target.value)} />
        </Alan>
        <Alan etiket={metin.ustTutarEtiketi} ipucu={ORTAK.tutar}>
          <input data-testid="margin-rule-max" inputMode="decimal" value={ustTutar} onChange={(olay) => setUstTutar(olay.target.value)} />
        </Alan>
        <Alan etiket={metin.bantParaBirimiEtiketi} ipucu={metin.bantParaBirimiIpucu}>
          <input data-testid="margin-rule-currency" value={bantParaBirimi} maxLength={3} onChange={(olay) => setBantParaBirimi(olay.target.value.toUpperCase())} />
        </Alan>
      </AlanSatiri>
      <AlanSatiri>
        <Alan etiket={metin.marjYuzdeEtiketi} ipucu={metin.marjYuzdeIpucu}>
          <input data-testid="margin-rule-margin-bps" inputMode="decimal" value={marjYuzde} onChange={(olay) => setMarjYuzde(olay.target.value)} />
        </Alan>
        <Alan etiket={metin.marjSabitEtiketi} ipucu={ORTAK.tutar}>
          <input data-testid="margin-rule-margin-fixed" inputMode="decimal" value={marjSabit} onChange={(olay) => setMarjSabit(olay.target.value)} />
        </Alan>
      </AlanSatiri>
      <button
        type="button"
        className="birincil"
        data-testid="margin-rule-create"
        onClick={() => void isle(async () => {
          if (ad.trim().length < 3) throw new Error(metin.adUyarisi);
          if (marjYuzde.trim() === '' && marjSabit.trim() === '') throw new Error(metin.marjUyarisi);
          if ((sezonBaslangic.trim() === '') !== (sezonBitis.trim() === '')) throw new Error(metin.sezonUyarisi);
          const altVar = altTutar.trim() !== '';
          const ustVar = ustTutar.trim() !== '';
          if ((altVar || ustVar) && bantParaBirimi.trim().length !== 3) throw new Error(metin.bantUyarisi);
          const olusan = await api.createMarginRule({
            name: ad.trim(),
            // Baz puan: %18,50 -> 1850. Ondalık hiçbir adımda taşınmıyor.
            ...(marjYuzde.trim() ? { marginBps: ondalikTamSayiya(marjYuzde, 2, metin.yuzdeBicimiUyarisi) } : {}),
            ...(marjSabit.trim() ? { marginFixedMinor: paraAltBirime(marjSabit) } : {}),
            ...(oncelik.trim() ? { priority: Number.parseInt(oncelik.trim(), 10) } : {}),
            ...(tur ? { itemType: tur } : {}),
            ...(destinasyon.trim() ? { destination: destinasyon.trim() } : {}),
            ...(sezonBaslangic.trim() ? { seasonStartMonth: Number.parseInt(sezonBaslangic.trim(), 10) } : {}),
            ...(sezonBitis.trim() ? { seasonEndMonth: Number.parseInt(sezonBitis.trim(), 10) } : {}),
            ...(segment.trim() ? { customerSegmentTag: segment.trim() } : {}),
            ...(altVar ? { minAmountMinor: paraAltBirime(altTutar) } : {}),
            ...(ustVar ? { maxAmountMinor: paraAltBirime(ustTutar) } : {}),
            ...(altVar || ustVar ? { bandCurrencyCode: bantParaBirimi.trim() } : {}),
            // Kararın alındığı an: uç bunu zorunlu tutuyor ve kaydın kendisine yazıyor.
            decidedAt: new Date().toISOString(),
          });
          setAd('');
          setOncelik('');
          setTur('');
          setDestinasyon('');
          setSezonBaslangic('');
          setSezonBitis('');
          setSegment('');
          setAltTutar('');
          setUstTutar('');
          setBantParaBirimi('');
          setMarjYuzde('');
          setMarjSabit('');
          return olusan;
        }, metin.olusturuldu(ad.trim()))}
      >
        {metin.olustur}
      </button>
      </details>
    </section>
  );
}

/**
 * Finans bölümünün ek yüzeyi: `SectionPage` bunu `FinanceWorkflowPanel`in **altına** ekliyor
 * (`App.tsx`, bolum === 'FINANCE'). Her alt bölüm kendi izniyle çiziliyor; izni olmayan
 * (satışçı gibi) için sekme bile çıkmıyor. Arkadaki her uç `finance:read`/`finance:write` ile
 * ayrıca korunuyor — burası yalnız kullanılamayacak bir düğmeyi göstermemek için.
 */
export function FinansEkstraPaneli({ me, view }: { me: Me; view?: FinanceView }) {
  const canRead = me.permissions.includes('finance:read');
  const canWrite = me.permissions.includes('finance:write');
  /**
   * Bildirim kuyruğunun iki düğmesi `coupon:write` istiyor, `finance:write` değil.
   *
   * İkisi aynı rolde birlikte bulunuyor (FINANCE) ama aynı şey değiller: ADMIN ve MANAGER
   * `finance:read` tutuyor, ikisini de tutmuyor. `canWrite`ı kullanmak kuyruk düğmelerini
   * yanlış izne bağlardı ve zaten bugünkü kusur tam olarak buydu — düğme hiçbir izne bağlı
   * değildi ve panelin sahibi ona basınca 403 alıyordu.
   */
  const kuyrukYazabilir = me.permissions.includes('coupon:write');
  /**
   * Marj kuralı kendi izniyle (`margin_rule:write`) çiziliyor ve finans okuma kapısının
   * **dışında**: bugün iki izni de aynı iki rol tutuyor, ama biri diğerinden türetilmiş değil ve
   * ikisini tek bir `if`e bağlamak, bir gün ayrıştıklarında sessizce yanlış cevap verirdi.
   */
  const marjYazabilir = me.permissions.includes('margin_rule:write');

  return (
    <>
      {canRead ? (
        <>
          {!view || view === 'invoices' ? <KesilecekFaturaBolumu canWrite={canWrite} /> : null}
          {!view || view === 'invoices' ? <AlisFaturalariBolumu canWrite={canWrite} /> : null}
          {!view || view === 'invoices' ? <SiparisFarkiBolumu /> : null}
          {!view || view === 'invoices' ? <KesilenFaturalarBolumu /> : null}
          {!view || view === 'suppliers' ? <TedarikciOdemesiBolumu canWrite={canWrite} /> : null}
          {!view || view === 'earnings' ? <HakedislerBolumu canWrite={canWrite} /> : null}
          {!view || view === 'coupons' ? <KuponBolumu kuponYazabilir={kuyrukYazabilir} /> : null}
          {!view || view === 'points' ? <GeziPuanBolumu /> : null}
        </>
      ) : null}
      {marjYazabilir && (!view || view === 'rules') ? <MarjKurallariBolumu canReadFinance={canRead} /> : null}
    </>
  );
}
